import { useEffect, useMemo, useState } from 'react';
import usePipeline from '@/hooks/usePipeline.js';
import usePipelineFilters, {
  applyFilters,
  distinctPartners,
  distinctAircraft,
  distinctAircraftTypes,
} from '@/hooks/usePipelineFilters.js';
import { ACTIVE_STAGES, TERMINAL_STAGES } from '@/services/pipeline.service.js';
import PipelineBoard from '@/components/pipeline/PipelineBoard.jsx';
import BoardFilters from '@/components/pipeline/BoardFilters.jsx';
import { Button } from '@/components/ui/button.jsx';
import { RefreshCw } from 'lucide-react';
import { fetchMe } from '@/lib/session.js';
import { mineFor } from '@/lib/mine.js';

// /pipeline — V1 default landing. Spec §1, §3.
//
// MINE IS THE POLICY'S PRODUCER, DECIDED ON THE SERVER (deals-door-1 T19, PLAN-FINAL 4.4). The page asks /auth/me who
// is signed in (never the token) and sends the scope; the server keeps the items. The shared login, or an identity
// that could not be read, has no Mine: the button is off with its sentence and Mine is never asked for. Nothing is read
// until /auth/me answered, so a board read as All is never shown under Mine.

export default function Pipeline() {
  const { filters, update, reset } = usePipelineFilters();
  const [who, setWho] = useState(null);
  useEffect(() => {
    let live = true;
    Promise.resolve(fetchMe()).catch(() => null).then((me) => { if (live) setWho(mineFor(me)); });
    return () => { live = false; };
  }, []);
  const scope = filters.scope === 'mine' && !(who && who.personal) ? 'all' : filters.scope;
  const { data, error, loading, refresh } = usePipeline({ includeClosed: filters.closed, scope, enabled: !!who });
  const shown = useMemo(() => ({ ...filters, scope }), [filters, scope]);

  // Apply range/search filters BEFORE column indexing so the counts on each StageColumn reflect filtered state.
  const filtered = useMemo(() => {
    if (!data?.raw) return null;
    const items = applyFilters(data.raw, filters);
    const stages = filters.closed ? [...ACTIVE_STAGES, ...TERMINAL_STAGES] : ACTIVE_STAGES;
    const byStage = {};
    for (const s of stages) byStage[s] = [];
    for (const item of items) {
      const col = item.deal_stage;
      if (byStage[col]) byStage[col].push(item);
    }
    return { ...data, byStage, raw: items };
  }, [data, filters]);

  // Partner directory aggregated from the UNFILTERED set so picking
  // one partner doesn't hide the rest. Carriers are now sourced from
  // the canonical Vendors module via /api/tracon/carriers — fetched
  // inside BoardFilters itself (Vision §V2 fix #1, 2026-05-20).
  const partners = useMemo(() => distinctPartners(data?.raw), [data]);
  const aircraft = useMemo(() => distinctAircraft(data?.raw), [data]);
  const aircraftTypes = useMemo(() => distinctAircraftTypes(data?.raw), [data]);

  const filterBar = (
    <BoardFilters
      filters={shown}
      update={update}
      reset={reset}
      totalCount={filtered?.raw?.length}
      partners={partners}
      aircraft={aircraft}
      aircraftTypes={aircraftTypes}
      mineOff={who && !who.personal ? who.sentence : ''}
    />
  );

  if (loading && !data) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-text-muted">
        Loading pipeline…
      </div>
    );
  }

  if (error && !data) {
    // The server's sentence when it gave one (a Mine it could not check is said so, never drawn empty).
    const said = error.body && error.body.message;
    return (
      <>
      {said ? filterBar : null}
      <div className="mx-auto max-w-md p-4 text-center">
        <p className="rounded-card border border-accent/40 bg-accent-soft px-4 py-3 text-sm text-accent">
          {said || <>Couldn&apos;t load the pipeline.</>}
          <br />
          <span className="font-mono text-xs">
            {error.code || error.message || 'unknown'}
          </span>
        </p>
        <Button onClick={refresh} variant="outline" className="mt-4">
          <RefreshCw size={16} strokeWidth={1.5} />
          Retry
        </Button>
      </div>
      </>
    );
  }

  return (
    <>
      {filterBar}
      <PipelineBoard
        data={filtered}
        sortBy={filters.sort}
        showClosed={filters.closed}
        onChange={refresh}
      />
    </>
  );
}
